import { useEffect } from 'react';
import { useSelectionStore } from '../stores/selectionStore';
import { useClipboard } from './useClipboard';
import { useUndoHistory } from './useUndoHistory';
import { isFilePasteTakeover } from '../utils/pasteCoordination';

interface KeyboardShortcutsOptions {
  // 由 EditorLayout 注入 undoHistory 实例（同一份，避免重复创建）
  undoHistory: ReturnType<typeof useUndoHistory>;
  clipboard: ReturnType<typeof useClipboard>;
  /** 图层编辑器等全屏编辑器打开期间停用全局监听（方案 §6 #21：防编辑器内 Delete 误删主画布节点、Ctrl+Z 撤销到 workflow） */
  enabled?: boolean;
}

export const useKeyboardShortcuts = ({ undoHistory, clipboard, enabled = true }: KeyboardShortcutsOptions) => {
  const { undo, saveHistory } = undoHistory;
  const { copy, paste, duplicate } = clipboard;

  useEffect(() => {
    if (!enabled) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;

      // Ctrl/Cmd + Z：撤销
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        undo();
        return;
      }

      // Ctrl/Cmd + C：复制（多选 + 内部连接，由 useClipboard 处理）
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'c') {
        const selectedArray = useSelectionStore.getState().getSelectedArray();
        if (selectedArray.length > 0) {
          e.preventDefault();
          copy();
        }
        return;
      }

      // Ctrl/Cmd + V：粘贴（多节点 + 连接重建，由 useClipboard 处理，内部已 saveHistory）
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'v') {
        // 不再无条件 preventDefault：让浏览器派发原生 paste 事件，由 FlowEditor 的 paste 监听统一裁决
        //  - 系统剪贴板有文件 → paste 监听接管上传（写接管标记），下方延迟回调跳过内部粘贴
        //  - 无文件 → paste 监听不动，此处延迟回调执行内部节点粘贴（行为与现状一致）
        // 时序保证：keydown 与其默认行为触发的 paste 事件同步派发，setTimeout 回调必然在其后执行
        setTimeout(() => {
          if (isFilePasteTakeover()) return;
          paste();
        }, 0);
        return;
      }

      // Ctrl/Cmd + D：原地复制粘贴（文档 3.2.4，一步完成，内部已 saveHistory）
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'd') {
        const selectedArray = useSelectionStore.getState().getSelectedArray();
        if (selectedArray.length > 0) {
          e.preventDefault();
          duplicate();
        }
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        useSelectionStore.getState().selectAll();
        return;
      }

      // Delete / Backspace：删除选中（文档 5.4 修复：补 saveHistory）
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const selectedArray = useSelectionStore.getState().getSelectedArray();
        if (selectedArray.length > 0) {
          e.preventDefault();
          // 删除前保存历史，修复原本不可撤销的 bug
          saveHistory();
          useSelectionStore.getState().deleteSelected();
        }
        return;
      }

      if (e.key === 'Escape') {
        useSelectionStore.getState().clearSelection();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, saveHistory, copy, paste, duplicate, enabled]);
};
